/**
 * RemakeAddToProductBridge — flag-gated entry point for transferring a
 * completed Remake onto a product.
 *
 * Flag ON  → new unified AddToProductOverlay (payload built by the pure
 *            remakeToAddToProductPayload mapper).
 * Flag OFF → legacy RemakeApplyOverlay (unchanged behaviour). The legacy
 *            import is intentionally kept — PR7 removes it once the flag is
 *            fully rolled out.
 *
 * Default: flag OFF → zero user-visible change.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Send } from 'lucide-react'
import {
  AddToProductOverlay,
  type AddToProductImage,
} from '@/components/v2/product/add-to-product'
import { useUnifiedAddToProductFlag } from '@/hooks/useUnifiedAddToProductFlag'
import { useImageSlots } from '@/hooks/useImageSlots'
import {
  RemakeApplyOverlay,
  type RemakeApplyOverlayResult,
} from './RemakeApply'
import { collectRemakeImageGroups } from './RemakeApply/remakeImages'
import {
  remakeToAddToProductPayload,
  type RemakeLike,
  type RemakeIdentifiers,
} from './remakeToAddToProduct'

export interface RemakeAddToProductBridgeProps {
  remake: RemakeLike | null
  identifiers: RemakeIdentifiers
  asin: string
  versionNumber: number
  completedAt: string
  remakeId: string | null
}

export function RemakeAddToProductBridge({
  remake,
  identifiers,
  asin,
  versionNumber,
  completedAt,
  remakeId,
}: RemakeAddToProductBridgeProps) {
  const { t } = useTranslation()
  const flagEnabled = useUnifiedAddToProductFlag()
  const [open, setOpen] = useState(false)

  // Hook called unconditionally at top level (React rules). Null/undefined
  // remakeId is safe — useImageSlots disables the query (mirrors
  // RemakeImagesPanel). The resolved images are only consumed on the
  // flag-ON path below.
  const { data: slots } = useImageSlots(remakeId ?? undefined)
  const remakeImages: AddToProductImage[] = collectRemakeImageGroups(
    slots,
  ).flatMap((group) =>
    group.images.map((img) => ({
      id: img.generationId,
      url: img.assetUrl,
      kind: 'generated' as const,
    })),
  )

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-orange-500 text-white hover:bg-orange-600"
      >
        <Send className="w-4 h-4" />
        {t('addToProduct.buttonOpen')}
      </button>

      {open &&
        (flagEnabled ? (
          <AddToProductOverlay
            payload={remakeToAddToProductPayload(
              remake,
              identifiers,
              t('addToProduct.buttonOpen'),
              remakeImages,
            )}
            onClose={() => setOpen(false)}
          />
        ) : (
          <RemakeApplyOverlay
            asin={asin}
            versionNumber={versionNumber}
            completedAt={completedAt}
            identifiers={identifiers}
            remakeResult={
              (remake?.text_result as unknown as RemakeApplyOverlayResult) ?? null
            }
            remakeId={remakeId}
            onClose={() => setOpen(false)}
          />
        ))}
    </>
  )
}
